import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';

const ImportProgressDialog = ({ 
  open, 
  jobId, 
  getStatusFn, 
  onComplete, 
  onClose,
  title = "Import en cours..."
}) => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !jobId) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await getStatusFn(jobId);
        setStatus(response.data);
        
        // If completed or failed, stop polling
        if (response.data.status === 'completed' || response.data.status === 'failed') {
          clearInterval(pollInterval);
          if (response.data.status === 'completed' && onComplete) {
            onComplete(response.data);
          }
        }
      } catch (err) {
        console.error('Erreur lors de la récupération du statut:', err);
        const errorMessage = err.response?.data?.detail 
          || err.response?.data?.message 
          || err.message 
          || 'Erreur lors de la récupération du statut';
        setError(errorMessage);
        clearInterval(pollInterval);
      }
    }, 1000); // Poll every second

    return () => clearInterval(pollInterval);
  }, [open, jobId, getStatusFn, onComplete]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Terminé';
      case 'failed': return 'Échoué';
      case 'processing': return 'En cours...';
      case 'pending': return 'En attente...';
      default: return status;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {status && (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Chip 
                label={getStatusLabel(status.status)} 
                color={getStatusColor(status.status)}
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                {status.processed_rows} / {status.total_rows} lignes
              </Typography>
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={status.progress_percentage || 0}
              sx={{ mb: 2 }}
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {status.message}
            </Typography>
            
            {status.status === 'completed' && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip label={`${status.created_count} créés`} color="success" size="small" />
                <Chip label={`${status.updated_count} mis à jour`} color="info" size="small" />
                {status.errors?.length > 0 && (
                  <Chip label={`${status.errors.length} erreurs`} color="warning" size="small" />
                )}
              </Box>
            )}
            
            {status.errors?.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Erreurs:
                </Typography>
                <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50' }}>
                  {status.errors.slice(-10).map((error, idx) => (
                    <ListItem key={idx}>
                      <ListItemText 
                        primary={error}
                        primaryTypographyProps={{ variant: 'caption', color: 'error' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={status?.status === 'processing'}>
          {status?.status === 'completed' || status?.status === 'failed' ? 'Fermer' : 'Annuler'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportProgressDialog;
